import { useEffect, useMemo } from 'react';

import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';

import {
  adminCreateShiftContract,
  adminPharmaciesInfo,
  adminResetValue,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  ContractTypes,
  convertToDecimals,
  createWorkshiftAdmin,
  getRecurrence,
  ProfessionalReplacementTypes,
  TravelAllowanceEnums,
} from '@pharmaplan/common';

import { handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';
import { makeReqParams } from '../../../helpers/workshift/functions';
import { DurationTypes } from '../../../helpers/Constants';
import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';
import { AdminWorkshiftValidator } from '../../../helpers/commonValidators';
import {
  parseEmptyStringProxy,
  parseFloatProxy,
} from '../../../helpers/proxyHandlers';
import useGetChangedContract from '../../LongTermContract/useGetChangedContract';
import useSyncWorkshiftValues from '../../workshift/useSyncWorkshiftValues';
import useSyncMultiHours from '../../workshift/useSyncMultiHours';
import {
  rateLongTermParamsValidation,
  rateValidation,
} from '../../../helpers/Functions';
import useSelfService from '../../useSelfService';
import useHourlyRates from '../useHourlyRates';

const { notRepeat } = DurationTypes;
const { accommodation, meal, emergency, others } = AllowanceEnums;
const {
  travelAllowanceType,
  accommodationAllowanceType,
  paidHourlyRate: paidHourlyRateEnum,
  accommodationAllowance,
  mealAllowance,
  travelAllowanceAmount,
  travelAllowanceRatePerKm,
  emergencyFees,
  otherAllowance,
  weekDayPaidHourlyRate,
  weekendPaidHourlyRate,
} = AllowancesNames;

interface IUseAdminCreateWorkshift {
  pharmacyId: string;
  name: string;
}

const useAdminCreateWorkshift = ({
  pharmacyId,
  name,
}: IUseAdminCreateWorkshift) => {
  const dispatch = useAppDispatch();

  const { isSelfService } = useSelfService();
  const { getChangedItems, comparator } = useGetChangedContract();
  const { userMDSRate, userRate, pharmacyUserMDSRate, pharmacyUserRate } = useHourlyRates({ isFromCalendar: true });

  const info = useAppSelector(adminPharmaciesInfo);
  const {
    pharmacyHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  } = info ?? {};

  const recurrence = useAppSelector(getRecurrence);
  const today = useMemo(() =>
    DateTime.now(), []);

  const onSubmit = (values: FormikValues) => {
    const {
      notes,
      pharmacyHourlyRate: formikPharmacyHourlyRate,
      paidHourlyRate,
      weekendPaidHourlyRate: weekRate,
      weekDayPaidHourlyRate: weekendRate,
      pharmacyWeekDayHourlyRate: pharmacyWeekdayRate,
      pharmacyWeekendHourlyRate: pharmacyWeekendRate,
      weekdayHours,
      weekendHours,
      replacementType,
    } = values ?? {};

    if (values?.contractType === ContractTypes.longTerm) {
      const { startDate, endDate } = comparator();

      const dateParams = {
        startDate,
        endDate,
      };

      const rates = {
        weekendPaidHourlyRate: weekRate,
        weekDayPaidHourlyRate: weekendRate,
        pharmacyWeekDayPaidHourlyRate: pharmacyWeekdayRate,
        pharmacyWeekendPaidHourlyRate: pharmacyWeekendRate,
      };

      const handledForEmptyString = new Proxy(rates, parseEmptyStringProxy);
      const handledForFloat = new Proxy(handledForEmptyString, parseFloatProxy);

      const isPharmacyLongParamsGreater = rateLongTermParamsValidation({
        professionalWeekday: values?.weekDayPaidHourlyRate,
        professionalWeekend: values?.weekendPaidHourlyRate,
        pharmacyWeekday: values?.pharmacyWeekDayHourlyRate,
        pharmacyWeekend: values?.pharmacyWeekendHourlyRate,
        dispatch,
        isSelfService,
        weekdayHours,
        weekendHours,
        replacementType,
      });

      if (!isPharmacyLongParamsGreater) {
        return;
      }

      dispatch(
        adminCreateShiftContract({
          pharmacyId,
          model: {
            ...makeReqParams(values, recurrence, dateParams),
            allowanceNotes: values?.allowanceNotes,
            isLongTermContract: values?.contractType === ContractTypes.longTerm,
            shiftContractAttributes: getChangedItems(),
            typeReplacement: values?.replacementType,
            note: notes,
            ...handledForFloat,
          },
        }),
      );
      return;
    }

    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKm],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmount],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal],
        values[mealAllowance],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceType]
          === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowance],
        null,
      ),
      paidHourlyRate: values[paidHourlyRateEnum],
      pharmacyHourlyRate: formikPharmacyHourlyRate,
      emergencyFees: handleWorkshiftToggles(
        values[emergency],
        values[emergencyFees],
        null,
      ),
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowance],
        null,
      ),
    };

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    const isPharmacyGreaterPharmacist = rateValidation({
      pharmacistRate: paidHourlyRate,
      pharmacyRate: formikPharmacyHourlyRate,
      isSelfService,
      dispatch,
      replacementType,
    });

    if (isPharmacyGreaterPharmacist) {
      dispatch(
        createWorkshiftAdmin({
          pharmacyId,
          model: {
            typeReplacement: values?.replacementType,
            travelAllowanceTypes: values[travelAllowanceType],
            accommodationAllowanceType: handleWorkshiftToggles(
              values[accommodation],
              values[accommodationAllowanceType],
              null,
            ),
            ...makeReqParams(values, recurrence),
            ...handledForDecimals,
            note: notes,
          },
        }),
      );
    }
  };

  const initialValues = {
    name,
    selectedDate: today,
    recurrence: notRepeat,
    startTime: null,
    endTime: null,
    notes: '',
    emergencyFees: '',
    mealAllowance: '',
    travelAllowance: '',
    accommodationAllowance: '',
    paidHourlyRate: 0,
    pharmacyHourlyRate: convertToDecimals(pharmacyHourlyRate),
    contractType: ContractTypes.shortTerm,
    [weekDayPaidHourlyRate]: 0,
    [weekendPaidHourlyRate]: 0,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    replacementType: ProfessionalReplacementTypes.pharmacist,
    weekdayHours: 0,
    weekendHours: 0,
    [travelAllowanceType]: [],
    [accommodationAllowanceType]: '',
    [travelAllowanceRatePerKm]: '',
    [travelAllowanceAmount]: '',
    [accommodationAllowance]: '',
    [mealAllowance]: '',
    [emergencyFees]: '',
    [otherAllowance]: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: AdminWorkshiftValidator(),
    enableReinitialize: true,
    onSubmit,
  });

  useSyncMultiHours(formik);
  useSyncWorkshiftValues(formik);

  useEffect(() => {
    const pharmacyRate = pharmacyUserRate?.[formik.values.replacementType];

    const { weekday, weekend } = userMDSRate?.[formik.values.replacementType] ?? {};
    const { weekday: pharmacyWeekday, weekend: pharmacyWeekend } = pharmacyUserMDSRate?.[formik.values.replacementType] ?? {};

    if (formik.values.contractType === ContractTypes.longTerm) {
      formik.setFieldValue('pharmacyWeekDayHourlyRate', pharmacyWeekday);
      formik.setFieldValue('pharmacyWeekendHourlyRate', pharmacyWeekend);

      formik.setFieldValue(weekDayPaidHourlyRate, weekday ?? 0);
      formik.setFieldValue(weekendPaidHourlyRate, weekend ?? 0);
    } else {
      formik.setFieldValue(
        'paidHourlyRate',
        userRate[formik.values.replacementType] ?? 0,
      );

      formik.setFieldValue('pharmacyHourlyRate', pharmacyRate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.replacementType,
    formik.values.contractType,
    userRate,
    userMDSRate,
    pharmacyUserMDSRate,
  ]);

  useEffect(() =>
    () => {
      dispatch(adminResetValue(['pharmaciesInfo']));
    }, []);

  return { formik };
};

export default useAdminCreateWorkshift;
